import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BottomNav from "@/components/BottomNav";
import BrandMark from "@/components/BrandMark";
import WeatherStrip from "@/components/WeatherStrip";
import Chip from "@/components/Chip";
import EventCard from "@/components/EventCard";
import { IDS } from "@/constants/testIds";
import { Sparkles, MessagesSquare, CalendarPlus } from "lucide-react";

const capitalize = (s) => (s ? s[0].toUpperCase() + s.slice(1) : "");

const OCCASIONS = ["Office", "Meeting", "Date", "Party", "Wedding", "Casual", "Travel"];
const MOODS = ["Confident", "Relaxed", "Low-key", "Festive"];

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [closetCount, setClosetCount] = useState(0);
  const [weather, setWeather] = useState(null);
  const [occasion, setOccasion] = useState("Office");
  const [mood, setMood] = useState("Confident");
  const [withWhom, setWithWhom] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [pending, setPending] = useState(null);
  const [events, setEvents] = useState([]);
  const [seasonNudge, setSeasonNudge] = useState(null);
  const [vibes, setVibes] = useState([]);
  const [vibe, setVibe] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: c }, { data: pend }, { data: evs }, { data: ss }, { data: vb }] = await Promise.all([
          api.get("/profile"),
          api.get("/closet"),
          api.get("/wear/pending").catch(() => ({ data: { pending: [] } })),
          api.get("/events/upcoming", { params: { limit: 3 } }).catch(() => ({ data: { events: [] } })),
          api.get("/season/status").catch(() => ({ data: null })),
          api.get("/vibes").catch(() => ({ data: { vibes: [], favorite: null } })),
        ]);
        setProfile(p);
        setClosetCount(c.count);
        if (pend?.pending?.length) setPending(pend.pending[0]);
        setEvents(evs?.events || []);
        if (ss?.show_transition_nudge) setSeasonNudge(ss);
        setVibes(vb.vibes || []);
        setVibe(vb.favorite || (vb.vibes && vb.vibes[0]?.id) || null);
        // Try live geolocation first, fall back to profile city
        const applyWeather = async (lat, lon) => {
          try {
            const { data: w } = await api.get("/weather", { params: { lat, lon } });
            setWeather({ ...w, lat, lon });
          } catch (err) {
            console.error("weather fetch failed", err);
          }
        };
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (pos) => applyWeather(pos.coords.latitude, pos.coords.longitude),
            () => { if (p.lat != null && p.lon != null) applyWeather(p.lat, p.lon); },
            { timeout: 5000, enableHighAccuracy: false }
          );
        } else if (p.lat != null && p.lon != null) {
          applyWeather(p.lat, p.lon);
        }
      } catch (err) {
        console.error("home load failed", err);
      }
    })();
  }, []);

  const styleMe = async () => {
    if (closetCount < 5) {
      toast.error(`Add ${5 - closetCount} more item(s) to unlock`);
      return;
    }
    setSuggesting(true);
    try {
      const { data } = await api.post("/suggest", {
        occasion,
        mood,
        vibe: vibe || undefined,
        with_whom: withWhom || undefined,
      });
      navigate("/outfit", { state: data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate a look. Try again.");
    } finally {
      setSuggesting(false);
    }
  };

  const confirmYes = async () => {
    if (!pending) return;
    await api.post("/wear/confirm", {
      suggestion_id: pending.suggestion_id,
      item_ids: pending.item_ids,
      occasion: pending.occasion,
      mood: pending.mood,
      confirmed: true,
    });
    setPending(null);
    toast.success("Logged — I&rsquo;ll remember this.");
  };

  return (
    <div className="min-h-[100dvh] bg-[#FBF9F6] grain" style={{ paddingBottom: "calc(6rem + env(safe-area-inset-bottom, 0.75rem))" }}>
      <div className="max-w-md mx-auto px-6 pt-12">
        <BrandMark className="mb-4" />
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-[Cormorant_Garamond] text-4xl leading-tight text-stone-900 mt-1">
              Hello, <em className="italic text-[#6B7A54]">{profile?.name || user?.name || "friend"}</em>
            </h1>
          </div>
        </div>
        <WeatherStrip weather={weather} city={profile?.city} lat={weather?.lat} lon={weather?.lon} />

        {seasonNudge && (
          <div className="mt-6 rounded-2xl border border-[#6B7A54]/30 bg-white p-5">
            <div className="text-[10px] font-[Manrope] font-bold tracking-[0.25em] uppercase text-[#6B7A54]">
              Season update · {seasonNudge.current_season}
            </div>
            <p className="mt-2 font-[Cormorant_Garamond] text-xl italic text-stone-900 leading-snug">
              {seasonNudge.previous_season ? `${capitalize(seasonNudge.previous_season)} to ${capitalize(seasonNudge.current_season)}` : `${capitalize(seasonNudge.current_season)} is here`} — {seasonNudge.in_season_count} items in your closet are back in rotation.
            </p>
            <button
              onClick={() => setSeasonNudge(null)}
              className="mt-3 px-4 py-2 rounded-full bg-stone-100 text-stone-700 text-xs font-[Manrope]"
            >
              Got it
            </button>
          </div>
        )}

        {pending && (
          <div className="mt-6 bg-stone-900 text-white rounded-3xl p-5 shadow-[0_16px_40px_rgba(28,25,23,0.18)]">
            <div className="text-[10px] font-[Manrope] font-bold tracking-[0.25em] uppercase text-[#F2EFEA]/80">
              Evening check-in
            </div>
            <p className="mt-2 font-[Cormorant_Garamond] text-lg italic">
              Did you wear the {pending.occasion?.toLowerCase()} look today?
            </p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={confirmYes}
                className="flex-1 bg-[#6B7A54] text-white rounded-full py-2.5 text-sm font-[Manrope] font-medium"
              >
                Yes
              </button>
              <button
                onClick={() => setPending(null)}
                className="flex-1 bg-white/10 text-white rounded-full py-2.5 text-sm font-[Manrope] font-medium"
              >
                Not really
              </button>
            </div>
          </div>
        )}

        {closetCount < 5 && (
          <div className="mt-6 bg-white border border-stone-100 rounded-2xl p-5 shadow-[0_2px_8px_rgba(28,25,23,0.04)]">
            <div className="text-[10px] font-[Manrope] font-bold tracking-[0.25em] uppercase text-[#6B7A54]">
              Unlock in {5 - closetCount}
            </div>
            <p className="mt-2 font-[Cormorant_Garamond] text-xl text-stone-900 italic">
              Add {5 - closetCount} more item{5 - closetCount > 1 ? "s" : ""} to unlock your first suggestion.
            </p>
            <button
              onClick={() => navigate("/closet")}
              className="mt-4 w-full bg-stone-900 text-white rounded-full py-3 font-[Manrope] font-medium text-sm hover:bg-black transition-colors"
              data-testid={IDS.homeCloset}
            >
              Add to closet ({closetCount}/5)
            </button>
          </div>
        )}

        <div className="mt-10">
          <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-stone-500 mb-3">
            Occasion
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-6 px-6 pb-1">
            {OCCASIONS.map((o) => (
              <Chip
                key={o}
                label={o}
                active={occasion === o}
                onClick={() => setOccasion(o)}
                testId={IDS.occasionChip(o.toLowerCase())}
              />
            ))}
          </div>
        </div>

        <div className="mt-8">
          <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-stone-500 mb-3">
            Mood
          </div>
          <div className="flex gap-2 flex-wrap">
            {MOODS.map((m) => (
              <Chip
                key={m}
                label={m}
                small
                active={mood === m}
                onClick={() => setMood(m)}
                testId={IDS.moodChip(m.toLowerCase())}
              />
            ))}
          </div>
        </div>

        <div className="mt-8">
          <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-stone-500 mb-3">
            How do you want to look today?
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-6 px-6 pb-1">
            {vibes.map((v) => (
              <button
                key={v.id}
                onClick={() => setVibe(v.id)}
                data-testid={`vibe-chip-${v.id}`}
                className={`flex-shrink-0 rounded-full border font-[Manrope] font-medium transition-colors active:scale-[0.97] px-4 py-2 text-sm flex items-center gap-1.5 ${
                  vibe === v.id
                    ? "bg-[#6B7A54] text-white border-transparent shadow-[0_8px_24px_rgba(107, 122, 84,0.18)]"
                    : "bg-white text-stone-700 border-stone-200 hover:bg-stone-50"
                }`}
              >
                <span>{v.emoji}</span>
                <span>{v.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <label className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-stone-500">
            Meeting (optional)
          </label>
          <input
            className="input mt-2"
            placeholder="e.g., Rahul, in-laws, client team"
            value={withWhom}
            onChange={(e) => setWithWhom(e.target.value)}
          />
        </div>

        <button
          data-testid={IDS.styleMeBtn}
          onClick={styleMe}
          disabled={suggesting}
          className="btn-primary mt-8"
        >
          <Sparkles size={16} strokeWidth={1.5} />
          {suggesting ? "Composing…" : "Style me now"}
        </button>

        <button
          data-testid={IDS.chatEntry}
          onClick={() => navigate("/chat")}
          className="w-full mt-3 flex items-center justify-center gap-2 py-4 rounded-full border border-stone-300 text-stone-800 font-[Manrope] font-medium hover:bg-stone-50 transition-colors"
        >
          <MessagesSquare size={16} strokeWidth={1.5} />
          Talk to your stylist
        </button>

        {/* Event Planner section (additive) */}
        <section className="mt-12">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-stone-500">
              Upcoming events
            </div>
            {events.length > 0 && (
              <button
                onClick={() => navigate("/events")}
                className="text-xs font-[Manrope] font-medium text-[#6B7A54] tracking-wide"
              >
                See all
              </button>
            )}
          </div>
          {events.length === 0 ? (
            <button
              onClick={() => navigate("/events/new")}
              className="w-full bg-white border border-dashed border-stone-300 rounded-2xl p-5 flex items-center gap-3 text-left hover:bg-stone-50 transition-colors"
            >
              <div className="h-10 w-10 rounded-full bg-[#6B7A54]/10 flex items-center justify-center text-[#6B7A54]">
                <CalendarPlus size={18} strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="font-[Cormorant_Garamond] italic text-lg text-stone-900 leading-tight">Plan an event</div>
                <div className="text-xs font-[Manrope] text-stone-500 mt-0.5">Wedding, trip, big meeting — I&rsquo;ll draft 3–4 looks.</div>
              </div>
            </button>
          ) : (
            <div className="flex flex-col gap-3">
              {events.map((e) => <EventCard key={e.event_id} event={e} compact />)}
              <button
                onClick={() => navigate("/events/new")}
                className="text-xs font-[Manrope] font-medium text-[#6B7A54] tracking-wide self-start px-3 py-2"
              >
                + plan another
              </button>
            </div>
          )}
        </section>
      </div>

      <BottomNav />
    </div>
  );
}
